import {
  collection,
  getDocs,
  limit,
  query,
  where,
} from 'firebase/firestore';
import { firestore } from 'services/firebase/client';
import type {
  PromotionalBanner,
  PromotionalBannerPage,
} from 'utils/types/PromotionalBanner';

const COLLECTION = 'promotional_banners';

const mapDocToPromotionalBanner = (
  id: string,
  data: Record<string, unknown>,
): PromotionalBanner | null => {
  if (!data.image || typeof data.image !== 'string') return null;

  const pages = Array.isArray(data.pages)
    ? data.pages.filter(
        (page): page is PromotionalBannerPage =>
          page === 'home' || page === 'beer-store',
      )
    : [];

  return {
    slug: typeof data.slug === 'string' ? data.slug : id,
    label: typeof data.label === 'string' ? data.label : id,
    image: data.image,
    alt: typeof data.alt === 'string' ? data.alt : undefined,
    storageKey:
      typeof data.storageKey === 'string'
        ? data.storageKey
        : `banner_closed_${id}`,
    pages,
    isCurrent: data.isCurrent === true,
    enabled: data.enabled !== false,
    ctaText: typeof data.ctaText === 'string' ? data.ctaText : undefined,
    ctaUrl: typeof data.ctaUrl === 'string' ? data.ctaUrl : undefined,
    startAt: data.startAt as PromotionalBanner['startAt'],
    endAt: data.endAt as PromotionalBanner['endAt'],
    createdAt: data.createdAt as PromotionalBanner['createdAt'],
    updatedAt: data.updatedAt as PromotionalBanner['updatedAt'],
  };
};

const isWithinSchedule = (banner: PromotionalBanner): boolean => {
  const now = Date.now();

  if (banner.startAt && banner.startAt.toMillis() > now) return false;
  if (banner.endAt && banner.endAt.toMillis() < now) return false;

  return true;
};

const isActiveForPage = (
  banner: PromotionalBanner,
  page: PromotionalBannerPage,
): boolean =>
  banner.enabled &&
  banner.isCurrent &&
  banner.pages.includes(page) &&
  isWithinSchedule(banner);

/**
 * Busca o banner promocional vigente para uma página do site.
 * Filtra no cliente para evitar índices compostos no Firestore.
 */
export async function getActivePromotionalBanner(
  page: PromotionalBannerPage,
): Promise<PromotionalBanner | null> {
  try {
    const coll = collection(firestore, COLLECTION);
    const q = query(coll, where('isCurrent', '==', true), limit(20));
    const snapshot = await getDocs(q);

    const activeBanner = snapshot.docs
      .map(docSnap =>
        mapDocToPromotionalBanner(docSnap.id, docSnap.data()),
      )
      .filter((banner): banner is PromotionalBanner => banner !== null)
      .find(banner => isActiveForPage(banner, page));

    return activeBanner ?? null;
  } catch (error) {
    console.error(`Erro ao buscar banner promocional (${page}):`, error);
    return null;
  }
}
